import { Logger } from '../support/Logger';

/**
 * rrweb (Mixpanel Session Replay) serializes the text of <style> elements
 * inside a shadow root but does NOT reconstruct constructable
 * adoptedStyleSheets for shadow roots attached on-demand after its initial
 * snapshot. Ionic/Stencil delivers ALL of an overlay's structural CSS via
 * adopted sheets -- and not just on the overlay host: a popover's content is
 * itself a tree of Ionic shadow components (ion-list/ion-item/ion-icon/...),
 * each with its own adopted sheet, all mounted on-demand. So the whole overlay
 * subtree is invisible to rrweb (the host positions but contents are unstyled).
 *
 * This discovers overlay hosts (attachShadow patch + an install-time sweep)
 * and mirrors every shadow root in the overlay's subtree's adopted rules into
 * a <style> inside that same shadow root, which rrweb does capture. It is inert
 * for live users: per the CSS scoping cascade a shadow root's own <style> is
 * ordered before its adoptedStyleSheets, so the still-present adopted sheets
 * win the tie. Stencil component adopted sheets are immutable post-hydration,
 * so each shadow root is mirrored exactly once. A per-overlay MutationObserver
 * (childList only) re-runs when content mounts after the host or the overlay
 * is reopened.
 */
export class OverlayShadowStyleMaterializer {
    static inject = () => [Logger];
    constructor(private log: Logger) {}

    private installed = false;

    // Only the overlay host must match; its whole subtree (incl. non-overlay
    // Ionic components inside a popover) is then handled wholesale. Components
    // elsewhere are captured in rrweb's full snapshot and are out of scope.
    private overlayTags = new Set([
        'ion-modal',
        'ion-popover',
        'ion-menu',
        'ion-action-sheet',
        'ion-alert',
        'ion-toast',
        'ion-loading',
        'ion-picker'
    ]);

    private readonly mirrorAttr = 'data-mp-shadow-style';

    private trackedHosts = new WeakSet<Element>();
    private observers = new WeakMap<Element, MutationObserver>();
    private mirroredRoots = new WeakSet<ShadowRoot>();
    private pending = new Set<Element>();
    private flushScheduled = false;

    install = () => {
        if (this.installed) {
            return;
        }
        const proto = typeof Element !== 'undefined' ? Element.prototype : undefined;
        if (!proto || typeof proto.attachShadow !== 'function') {
            return;
        }
        this.installed = true;

        const originalAttachShadow = proto.attachShadow;
        const onShadowAttached = this.onShadowAttached;

        proto.attachShadow = function (this: Element, init: ShadowRootInit): ShadowRoot {
            const shadowRoot = originalAttachShadow.call(this, init);
            onShadowAttached(this);
            return shadowRoot;
        };

        this.sweepExisting(document);

        this.log.info('OverlayShadowStyleMaterializer: installed');
    };

    private onShadowAttached = (host: Element): void => {
        if (!this.overlayTags.has(host.tagName.toLowerCase())) {
            return;
        }
        this.trackHost(host);
    };

    private trackHost = (host: Element): void => {
        if (this.trackedHosts.has(host)) {
            return;
        }
        this.trackedHosts.add(host);

        // Class/attribute changes don't change which adopted sheets exist (and
        // rrweb records them itself); only watch for content actually mounting.
        if (typeof MutationObserver === 'function') {
            const observer = new MutationObserver(() => {
                this.enqueue(host);
            });
            observer.observe(host, { childList: true, subtree: true });
            this.observers.set(host, observer);
        }

        this.enqueue(host);
    };

    private enqueue = (host: Element): void => {
        this.pending.add(host);
        if (this.flushScheduled) {
            return;
        }
        this.flushScheduled = true;
        const flush = () => {
            this.flushScheduled = false;
            const hosts = Array.from(this.pending);
            this.pending.clear();
            for (const queuedHost of hosts) {
                this.materializeSubtree(queuedHost);
                if (!queuedHost.isConnected) {
                    this.teardown(queuedHost);
                }
            }
        };
        if (typeof requestAnimationFrame === 'function') {
            requestAnimationFrame(flush);
        } else {
            setTimeout(flush, 0);
        }
    };

    private teardown = (host: Element): void => {
        const observer = this.observers.get(host);
        if (observer) {
            observer.disconnect();
            this.observers.delete(host);
        }
        this.trackedHosts.delete(host);
    };

    private materializeSubtree = (host: Element): void => {
        const walk = (root: ParentNode) => {
            for (const el of Array.from(root.querySelectorAll('*'))) {
                if (el.shadowRoot) {
                    this.mirror(el.shadowRoot);
                    walk(el.shadowRoot);
                }
            }
        };
        if (host.shadowRoot) {
            this.mirror(host.shadowRoot);
            walk(host.shadowRoot);
        }
        walk(host);
    };

    private sweepExisting = (root: Document | ShadowRoot | DocumentFragment): void => {
        for (const el of Array.from(root.querySelectorAll('*'))) {
            if (this.overlayTags.has(el.tagName.toLowerCase())) {
                this.trackHost(el);
            }
            if (el.shadowRoot) {
                this.sweepExisting(el.shadowRoot);
            }
            // Ionic keeps not-yet-presented overlays inside a <template>, whose
            // content is an inert fragment querySelectorAll won't descend into.
            if (el instanceof HTMLTemplateElement) {
                this.sweepExisting(el.content);
            }
        }
    };

    private mirror = (shadowRoot: ShadowRoot): void => {
        // Stencil adopted sheets are immutable post-hydration, so a root only
        // needs mirroring once; skip the cssRules serialization on re-walks.
        if (this.mirroredRoots.has(shadowRoot)) {
            return;
        }
        const sheets = shadowRoot.adoptedStyleSheets;
        if (!sheets || sheets.length === 0) {
            return;
        }
        let css = '';
        try {
            for (const sheet of sheets) {
                for (const rule of Array.from(sheet.cssRules)) {
                    css += rule.cssText + '\n';
                }
            }
        } catch {
            // cssRules throws SecurityError for a cross-origin stylesheet;
            // not expected for same-origin constructable sheets, and there is
            // nothing readable to mirror or usefully log.
            return;
        }
        if (!css) {
            return;
        }
        const mirror = document.createElement('style');
        mirror.setAttribute(this.mirrorAttr, '');
        mirror.textContent = css;
        // First child so the mirror stays lowest-priority in the shadow tree;
        // the adopted sheets still win the cascade tie for live users.
        shadowRoot.insertBefore(mirror, shadowRoot.firstChild);
        this.mirroredRoots.add(shadowRoot);
    };
}
